// src/services/capacityMonitor.ts
// Historical-baseline capacity monitoring for ND learners
// Uses past performance data to detect real-time struggles

import { analytics } from './analytics'

interface InteractionData {
  timestamp: number
  correct: boolean
  responseTime: number // milliseconds
  taskType: string // 'word-building', 'sentence-building', 'story-reading'
  difficulty: number // 1-3
  toolsUsed: string[] // ['tts', 'hints', 'reset']
}

interface HistoricalBaseline {
  avgResponseTime: number
  avgAccuracy: number
  avgHintsPerTask: number
  avgResetsPerTask: number
  typicalToolUsage: string[]
  dataPoints: number
  source: 'historical' | 'population' | 'current_session'
}

interface CapacityAlert {
  level: 'subtle' | 'moderate' | 'urgent'
  message: string
  action: 'simplify' | 'offer_easier' | 'suggest_break'
  showCalmCorner: boolean
  reasoning: string // for debugging
}

// Population norms by mood (fallback when no historical data)
const POPULATION_NORMS = {
  overwhelmed: {
    avgResponseTime: 25000, // 25 seconds per word
    avgAccuracy: 0.60,
    avgHintsPerTask: 2.0,
    avgResetsPerTask: 1.5
  },
  steady: {
    avgResponseTime: 18000, // 18 seconds
    avgAccuracy: 0.75,
    avgHintsPerTask: 1.0,
    avgResetsPerTask: 0.5
  },
  'high-energy': {
    avgResponseTime: 12000, // 12 seconds
    avgAccuracy: 0.80,
    avgHintsPerTask: 0.5,
    avgResetsPerTask: 0.3
  }
}

class CapacityMonitor {
  private interactions: InteractionData[] = []
  private actualBaseline: HistoricalBaseline | null = null
  private childId: string | null = null
  private currentMood: 'overwhelmed' | 'steady' | 'high-energy' = 'steady'
  
  private sessionStart: number = Date.now()
  private lastAlertTime: number = 0
  private readonly ALERT_COOLDOWN = 120000 // 2 minutes between alerts
  private declaredMoodBaseline: HistoricalBaseline | null = null
  private isStateMismatch: boolean = false

  // Initialize with child ID and load their historical baseline
  async initialize(childId: string, mood: 'overwhelmed' | 'steady' | 'high-energy' = 'steady') {
    this.childId = childId
    this.currentMood = mood
    this.interactions = []
    this.lastAlertTime = 0

    // Store declared mood baseline
    this.declaredMoodBaseline = this.getPopulationBaseline()
    
    // Load historical baseline
    await this.loadHistoricalBaseline()
    
    // ALWAYS use declared/population baseline for now until we have better historical tracking
    this.actualBaseline = this.declaredMoodBaseline
    console.log('📊 Using population baseline:', this.actualBaseline)
    
    console.log('🎯 Capacity Monitor initialized:', {
      childId,
      mood,
      baseline: this.actualBaseline
    })
  }

  // Load child's historical performance data
  private async loadHistoricalBaseline() {
    if (!this.childId) {
      this.actualBaseline = this.getPopulationBaseline()
      return
    }

    try {
      // Get child's progress data from your analytics system
      const progress = await analytics.getProgress()
      
      if (!progress || progress.totalSessions < 2) {
        // Not enough data - use population norms
        console.log('📊 No historical data, using population baseline')
        this.actualBaseline = this.getPopulationBaseline()
        return
      }

      // Calculate historical baseline from their past performance
      // This is a simplified version - you could enhance this by storing
      // more detailed metrics in your analytics system
      const estimatedAvgTime = this.estimateAvgTimeFromProgress(progress)
      const estimatedAccuracy = this.estimateAccuracyFromProgress(progress)
      
      this.actualBaseline = {
        avgResponseTime: estimatedAvgTime,
        avgAccuracy: estimatedAccuracy,
        avgHintsPerTask: 1.0, // Could track this in analytics
        avgResetsPerTask: 0.5, // Could track this in analytics
        typicalToolUsage: [], // Could track this in analytics
        dataPoints: progress.totalSessions,
        source: 'historical'
      }
      
      console.log('📊 Loaded historical baseline:', this.actualBaseline)
      
    } catch (error) {
      console.warn('Failed to load historical baseline, using population norms:', error)
      this.actualBaseline = this.getPopulationBaseline()
    }
  }

  // Estimate average time based on total minutes and sessions
  private estimateAvgTimeFromProgress(progress: any): number {
    // Rough estimate: totalMinutes / totalSessions / typical words per session
    const avgSessionMinutes = progress.totalMinutes / Math.max(progress.totalSessions, 1)
    const wordsPerSession = 6 // average
    const avgTimePerWord = (avgSessionMinutes * 60000) / wordsPerSession // convert to ms
    return Math.min(avgTimePerWord, 60000) // cap at 60 seconds
  }

  // Estimate accuracy from strengths vs practicing areas
  private estimateAccuracyFromProgress(progress: any): number {
    const strengths = progress.strengths?.length || 0
    const practicing = progress.practicingAreas?.length || 0
    
    // More strengths = higher accuracy estimate
    if (strengths > practicing * 2) return 0.80
    if (strengths > practicing) return 0.70
    if (strengths === practicing) return 0.60
    return 0.50
  }

  // Get population-based baseline when no historical data exists
  private getPopulationBaseline(): HistoricalBaseline {
    const norms = POPULATION_NORMS[this.currentMood] || POPULATION_NORMS.steady
    return {
      avgResponseTime: norms.avgResponseTime,
      avgAccuracy: norms.avgAccuracy,
      avgHintsPerTask: norms.avgHintsPerTask,
      avgResetsPerTask: norms.avgResetsPerTask,
      typicalToolUsage: [],
      dataPoints: 0,
      source: 'population'
    }
  }

  // Validate declared state after first 2 interactions
  private validateDeclaredState(): void {
    if (this.interactions.length !== 2 || !this.declaredMoodBaseline) return
    
    const currentAvgTime = this.calculateAvgResponseTime(this.interactions)
    const currentAccuracy = this.calculateAccuracy(this.interactions)
    
    const expectedTime = this.declaredMoodBaseline.avgResponseTime
    const expectedAccuracy = this.declaredMoodBaseline.avgAccuracy
    
    // Check if actual performance significantly differs from declared mood expectations
    const timeDeviation = Math.abs(currentAvgTime - expectedTime) / expectedTime
    const accuracyDeviation = Math.abs(currentAccuracy - expectedAccuracy)
    
    // Mismatch threshold: 40% time difference OR 30% accuracy difference
    if (timeDeviation > 0.4 || accuracyDeviation > 0.3) {
      console.log('⚠️ State mismatch detected:', {
        declared: this.currentMood,
        expectedTime,
        actualTime: currentAvgTime,
        deviation: `${(timeDeviation * 100).toFixed()}%`
      })
      
      this.isStateMismatch = true
      
      // Build session baseline from first 2 interactions
      this.actualBaseline = {
        avgResponseTime: currentAvgTime,
        avgAccuracy: currentAccuracy,
        avgHintsPerTask: this.calculateAvgHints(this.interactions),
        avgResetsPerTask: this.calculateAvgResets(this.interactions),
        typicalToolUsage: [],
        dataPoints: 2,
        source: 'current_session'
      }
      
      console.log('🔄 Adjusted to session baseline:', this.actualBaseline)
    }
  }

  // Record each interaction - now works from word 1
  recordInteraction(data: Omit<InteractionData, 'timestamp'>): CapacityAlert | null {
    const interaction: InteractionData = {
      ...data,
      timestamp: Date.now()
    }
    
    this.interactions.push(interaction)
    // Validate declared state after 2 interactions
    this.validateDeclaredState()
    
    // Start checking capacity immediately if we have historical baseline
    // OR after 2 interactions for first-time users (early detection)
    const canCheck = this.actualBaseline?.source === 'historical' || 
                     this.actualBaseline?.source === 'population' ||
                     this.interactions.length >= 2
    
    if (canCheck) {
      return this.checkCapacity()
    }
    
    return null
  }

  private checkCapacity(): CapacityAlert | null {
    if (!this.actualBaseline) return null
    
    // Special handling for overwhelmed mode (4-word sessions)
    if (this.currentMood === 'overwhelmed' && this.interactions.length <= 4) {
      return this.checkCapacityShortSession()
    }
    // Don't alert if we just alerted
    if (Date.now() - this.lastAlertTime < this.ALERT_COOLDOWN) {
      return null
    }

    // Look at last 3 interactions (or all if less than 3)
    const windowSize = Math.min(3, this.interactions.length)
    const recent = this.interactions.slice(-windowSize)
    
    if (recent.length === 0) return null

    // Calculate current performance metrics
    const currentAvgTime = this.calculateAvgResponseTime(recent)
    const currentAccuracy = this.calculateAccuracy(recent)
    const currentAvgHints = this.calculateAvgHints(recent)
    const currentAvgResets = this.calculateAvgResets(recent)
    
    // Calculate deviations from baseline
    const timeDeviation = (currentAvgTime - this.actualBaseline.avgResponseTime) / this.actualBaseline.avgResponseTime
    const accuracyDrop = this.actualBaseline.avgAccuracy - currentAccuracy
    const hintsIncrease = currentAvgHints - this.actualBaseline.avgHintsPerTask
    const resetsIncrease = currentAvgResets - this.actualBaseline.avgResetsPerTask
    
    // EARLY WARNING: Detect degradation from THEIR OWN recent performance
    // For first-time users with no historical data
    if (this.actualBaseline.source === 'current_session' && this.interactions.length >= 3) {
      const earlyInteractions = this.interactions.slice(0, 2)
      const earlyAvgTime = this.calculateAvgResponseTime(earlyInteractions)
      const earlyAccuracy = this.calculateAccuracy(earlyInteractions)
      
      const recentSlowdown = (currentAvgTime - earlyAvgTime) / earlyAvgTime
      const recentAccuracyDrop = earlyAccuracy - currentAccuracy
      
      if (recentSlowdown > 0.5 || recentAccuracyDrop > 0.3) {
        this.lastAlertTime = Date.now()
        return {
          level: 'moderate',
          message: 'Want to try something a bit easier right now?',
          action: 'offer_easier',
          showCalmCorner: false,
          reasoning: `Session degradation: ${Math.round(recentSlowdown * 100)}% slower, ${Math.round(recentAccuracyDrop * 100)}% accuracy drop`
        }
      }
    }
    
    // TIER 1: SUBTLE (20% slower OR 15% accuracy drop OR tool increase)
    if (timeDeviation > 0.2 || accuracyDrop > 0.15 || hintsIncrease > 1 || resetsIncrease > 0.5) {
      this.lastAlertTime = Date.now()
      return {
        level: 'subtle',
        message: '', // Silent adjustment
        action: 'simplify',
        showCalmCorner: false,
        reasoning: `Tier 1: time=${Math.round(timeDeviation*100)}%, acc=${Math.round(accuracyDrop*100)}%, hints=+${hintsIncrease.toFixed(1)}`
      }
    }
    
    // TIER 2: MODERATE (40% slower OR 25% accuracy drop OR significant tool use)
    if (timeDeviation > 0.4 || accuracyDrop > 0.25 || hintsIncrease > 1.5 || resetsIncrease > 1) {
      this.lastAlertTime = Date.now()
      return {
        level: 'moderate',
        message: 'Want to try something a bit easier right now?',
        action: 'offer_easier',
        showCalmCorner: false,
        reasoning: `Tier 2: time=${Math.round(timeDeviation*100)}%, acc=${Math.round(accuracyDrop*100)}%, hints=+${hintsIncrease.toFixed(1)}`
      }
    }
    
    // TIER 3: URGENT (60% slower OR 40% accuracy drop)
    if (timeDeviation > 0.6 || accuracyDrop > 0.4) {
      this.lastAlertTime = Date.now()
      return {
        level: 'urgent',
        message: 'I notice this is getting harder. Want to try something different or take a brain break?',
        action: 'suggest_break',
        showCalmCorner: true,
        reasoning: `Tier 3: time=${Math.round(timeDeviation*100)}%, acc=${Math.round(accuracyDrop*100)}%`
      }
    }
    
    return null
  }

  // Gentle capacity check for short overwhelmed sessions
  private checkCapacityShortSession(): CapacityAlert | null {
    if (Date.now() - this.lastAlertTime < this.ALERT_COOLDOWN) {
      return null
    }
    
    const recent = this.interactions.slice(-2)
    if (recent.length < 2) return null
    
    // Only intervene on clear distress signals
    const bothUsedTools = recent.every(i => i.toolsUsed.length > 0)
    const gettingSlower = recent[1].responseTime > recent[0].responseTime * 1.5
    const bothIncorrect = recent.every(i => !i.correct)
    
    if ((bothUsedTools && gettingSlower) || bothIncorrect) {
      this.lastAlertTime = Date.now()
      return {
        level: 'moderate',
        message: 'Want to try something a bit different?',
        action: 'offer_easier',
        showCalmCorner: false,
        reasoning: `Short session: Tools=${bothUsedTools}, Slowing=${gettingSlower}, Accuracy=${!bothIncorrect}`
      }
    }
    
    return null
  }

  private calculateAvgResponseTime(interactions: InteractionData[]): number {
    if (interactions.length === 0) return 0
    const sum = interactions.reduce((acc, i) => acc + i.responseTime, 0)
    return sum / interactions.length
  }

  private calculateAccuracy(interactions: InteractionData[]): number {
    if (interactions.length === 0) return 0
    const correct = interactions.filter(i => i.correct).length
    return correct / interactions.length
  }

  private calculateAvgHints(interactions: InteractionData[]): number {
    if (interactions.length === 0) return 0
    const totalHints = interactions.reduce((acc, i) => 
      acc + (i.toolsUsed.includes('hints') ? 1 : 0), 0
    )
    return totalHints / interactions.length
  }

  private calculateAvgResets(interactions: InteractionData[]): number {
    if (interactions.length === 0) return 0
    const totalResets = interactions.reduce((acc, i) => 
      acc + (i.toolsUsed.includes('reset') ? 1 : 0), 0
    )
    return totalResets / interactions.length
  }

  // Reset for new activity
  resetForNewActivity() {
    this.interactions = []
    this.lastAlertTime = 0
    console.log('🔄 Capacity monitor reset for new activity')
  }

  // Get current session duration
  getSessionDuration(): number {
    return Math.floor((Date.now() - this.sessionStart) / 60000) // minutes
  }

  // Check if session is getting long
  isSessionLong(): boolean {
    return this.getSessionDuration() > 15 // more than 15 minutes
  }

  // For testing: Force update baseline (temporary - remove in production)
  _TEST_setBaseline(baseline: Partial<HistoricalBaseline>) {
    this.actualBaseline = { ...this.actualBaseline!, ...baseline }
    console.log('🧪 TEST: Baseline updated:', this.actualBaseline)
  }
}

export const capacityMonitor = new CapacityMonitor()
export type { InteractionData, CapacityAlert, HistoricalBaseline }